import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { NativeSelect } from "@/components/ui/native-select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import {
  Plus, ArrowLeft, Edit, Trash2, Building, Search, Phone, Mail,
  MapPin, User, Loader2, Eye, FileText, Calendar, AlertTriangle,
  Shield, ShieldCheck, ShieldAlert, Clock, DollarSign, Briefcase,
  ChevronRight, GitBranch, Upload, X
} from "lucide-react";

// ==================== MASKS ====================
function maskCNPJ(v) { return v.replace(/\D/g,"").replace(/^(\d{2})(\d)/,"$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/,"$1.$2.$3").replace(/\.(\d{3})(\d)/,".$1/$2").replace(/(\d{4})(\d)/,"$1-$2").slice(0,18); }
function maskCPF(v) { return v.replace(/\D/g,"").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d{1,2})$/,"$1-$2").slice(0,14); }
function maskPhone(v) { const d=v.replace(/\D/g,""); if(d.length<=10) return d.replace(/(\d{2})(\d)/,"($1) $2").replace(/(\d{4})(\d)/,"$1-$2"); return d.replace(/(\d{2})(\d)/,"($1) $2").replace(/(\d{5})(\d)/,"$1-$2").slice(0,15); }
function maskCEP(v) { return v.replace(/\D/g,"").replace(/(\d{5})(\d)/,"$1-$2").slice(0,9); }
function maskIE(v) { return v.replace(/[^0-9./-]/g,"").slice(0,20); }

const tiposFornecimentoDefault = [
  { value: "eletrica", label: "Elétrica" },
  { value: "civil", label: "Civil" },
  { value: "hidraulica", label: "Hidráulica" },
  { value: "estrutura_metalica", label: "Estrutura Metálica" },
  { value: "acabamento", label: "Acabamento" },
  { value: "seguranca", label: "Equipamento de Segurança" },
  { value: "pintura", label: "Pintura" },
  { value: "impermeabilizacao", label: "Impermeabilização" },
  { value: "climatizacao", label: "Climatização" },
];

function formatDoc(doc) {
  if (!doc) return "";
  const d = doc.replace(/\D/g, "");
  if (d.length === 11) return maskCPF(d);
  if (d.length === 14) return maskCNPJ(d);
  return doc;
}

function isDocExpiringSoon(dateStr) {
  if (!dateStr) return false;
  try { const diff = (new Date(dateStr) - new Date()) / 86400000; return diff <= 30; } catch { return false; }
}

// ==================== LIST ====================
function EntidadesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => { fetchItems(); }, [filterTipo]);

  const fetchItems = async () => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      let url = `${API}/clientes-fornecedores`;
      if (filterTipo && filterTipo !== "all") url += `?tipo=${filterTipo}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      const token = sessionStorage.getItem("luvcorp_token");
      await axios.delete(`${API}/clientes-fornecedores/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Registro excluído");
      fetchItems();
    } catch { toast.error("Erro ao excluir"); }
  };

  const filtered = items.filter(i =>
    (i.nome || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (i.documento || "").includes(searchTerm) ||
    (i.razao_social || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-luvcorp-navy" /></div>;

  return (
    <div className="space-y-4" data-testid="entidades-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-[#0A1628]">Cadastro de Entidades</h1>
        <Link to="/cadastros/clientes-fornecedores/new">
          <Button className="bg-[#0A1628] hover:bg-[#0A1628]/90" data-testid="btn-novo-cadastro"><Plus className="h-4 w-4 mr-2" />Novo Cadastro</Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar por nome, documento..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-input" />
        </div>
        <NativeSelect value={filterTipo} onValueChange={setFilterTipo} className="w-full sm:w-[180px]" options={[
          { value: "all", label: "Todos" }, { value: "cliente", label: "Clientes" }, { value: "fornecedor", label: "Fornecedores" }, { value: "ambos", label: "Ambos" },
        ]} />
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <Card className="border-0 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#0A1628] hover:bg-[#0A1628]">
                <TableHead className="text-white font-medium">Nome / Razão Social</TableHead>
                <TableHead className="text-white font-medium">Documento</TableHead>
                <TableHead className="text-white font-medium">Tipo</TableHead>
                <TableHead className="text-white font-medium">Cidade/UF</TableHead>
                <TableHead className="text-white font-medium">Status</TableHead>
                <TableHead className="text-white font-medium text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(item => (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell>
                    <p className="font-medium text-sm">{item.nome}</p>
                    {item.nome_fantasia && item.nome_fantasia !== item.nome && <p className="text-xs text-slate-500">{item.nome_fantasia}</p>}
                  </TableCell>
                  <TableCell className="text-sm font-mono">{formatDoc(item.documento)}</TableCell>
                  <TableCell>
                    <Badge className={item.tipo_cadastro === "cliente" ? "bg-blue-100 text-blue-700" : item.tipo_cadastro === "fornecedor" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"}>
                      {item.tipo_cadastro === "cliente" ? "Cliente" : item.tipo_cadastro === "fornecedor" ? "Fornecedor" : "Ambos"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{item.cidade ? `${item.cidade}/${item.estado}` : "-"}</TableCell>
                  <TableCell>
                    {item.risco_alto && <Badge className="bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]"><AlertTriangle className="h-3 w-3 mr-1" />Risco</Badge>}
                    {item.homologacao_status === "ativo" && <Badge className="bg-emerald-100 text-emerald-700">Ativo</Badge>}
                    {item.homologacao_status === "bloqueado" && <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/cadastros/clientes-fornecedores/${item.id}`}><Button variant="ghost" size="icon" className="h-8 w-8"><Eye className="h-4 w-4" /></Button></Link>
                      <Link to={`/cadastros/clientes-fornecedores/${item.id}/edit`}><Button variant="ghost" size="icon" className="h-8 w-8"><Edit className="h-4 w-4" /></Button></Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Confirmar exclusão?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-red-600">Excluir</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filtered.map(item => (
          <Card key={item.id} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.nome}</p>
                  <p className="text-xs text-slate-500 font-mono">{formatDoc(item.documento)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={item.tipo_cadastro === "cliente" ? "bg-blue-100 text-blue-700 text-xs" : "bg-amber-100 text-amber-700 text-xs"}>
                      {item.tipo_cadastro === "cliente" ? "Cliente" : item.tipo_cadastro === "fornecedor" ? "Fornecedor" : "Ambos"}
                    </Badge>
                    {item.risco_alto && <Badge className="bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C] text-xs"><AlertTriangle className="h-3 w-3" /></Badge>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/cadastros/clientes-fornecedores/${item.id}`}><Button variant="ghost" size="icon" className="h-8 w-8"><Eye className="h-4 w-4" /></Button></Link>
                  <Link to={`/cadastros/clientes-fornecedores/${item.id}/edit`}><Button variant="ghost" size="icon" className="h-8 w-8"><Edit className="h-4 w-4" /></Button></Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {filtered.length === 0 && <p className="text-center text-slate-400 py-8">Nenhum registro encontrado</p>}
    </div>
  );
}

// ==================== FORM (3 TABS) ====================
function EntidadeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState({ cnpj: false, cep: false });
  const [activeTab, setActiveTab] = useState("geral");
  const [entidades, setEntidades] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tiposFornecimento, setTiposFornecimento] = useState(tiposFornecimentoDefault);
  const [novoTipoFornecimento, setNovoTipoFornecimento] = useState("");
  const [novoCargo, setNovoCargo] = useState("");

  const [form, setForm] = useState({
    tipo_pessoa: "juridica", tipo_cadastro: "cliente",
    nome: "", documento: "", razao_social: "", nome_fantasia: "",
    inscricao_estadual: "", inscricao_municipal: "", data_abertura: "", cnpj_status: "",
    cep: "", endereco: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "",
    telefone_whatsapp_1: "", telefone_whatsapp_2: "", email: "",
    pessoa_contato: "", cargo_contato: "",
    setor_cliente: "", esfera: "", poder: "", unidade_especifica: "",
    representante_nome: "", representante_telefone: "", representante_email: "",
    categoria_fornecedor: "", tipos_fornecimento: [],
    is_prestador_servicos: false, especializacao_prestador: "",
    homologacao_status: "", doc_cnd_federal: "", doc_cnd_fgts: "",
    doc_cnd_trabalhista: "", doc_pgr: "", doc_pcmat: "",
    documentos_homologacao: [], documentos_cliente: [],
    condicao_pagamento: "", banco: "", agencia: "", conta: "",
    tipo_pix: "", pix: "",
    email_financeiro: "", contato_financeiro: "",
    matriz_id: "",
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Load support data
    Promise.all([
      axios.get(`${API}/clientes-fornecedores`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/cargos-contato`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API}/bancos-brasil`, { headers }).catch(() => ({ data: [] })),
    ]).then(([entRes, cargosRes, bancosRes]) => {
      const ents = Array.isArray(entRes.data) ? entRes.data : [];
      setEntidades(ents.filter(e => e && e.id !== id));
      setCargos(Array.isArray(cargosRes.data) ? cargosRes.data : []);
      setBancos(Array.isArray(bancosRes.data) ? bancosRes.data : []);
    }).catch((err) => {
      console.warn("Erro ao carregar dados de apoio:", err);
    });

    if (isEdit) {
      axios.get(`${API}/clientes-fornecedores/${id}`, { headers })
        .then(res => {
          const d = res.data || {};
          setForm(prev => ({
            ...prev,
            ...d,
            tipos_fornecimento: Array.isArray(d.tipos_fornecimento) ? d.tipos_fornecimento : [],
            documentos_homologacao: Array.isArray(d.documentos_homologacao) ? d.documentos_homologacao : [],
            documentos_cliente: Array.isArray(d.documentos_cliente) ? d.documentos_cliente : [],
          }));
          setLoading(false);
        })
        .catch(() => { toast.error("Erro ao carregar"); navigate("/cadastros/clientes-fornecedores"); });
    }
  }, [id]);

  const lookupCNPJ = async () => {
    const doc = form.documento.replace(/\D/g, "");
    if (doc.length !== 14) { toast.error("CNPJ deve ter 14 dígitos"); return; }
    setLookupLoading(p => ({ ...p, cnpj: true }));
    try {
      const res = await axios.get(`${API}/lookup/cnpj/${doc}`, { headers });
      const d = res.data;
      setForm(prev => ({ ...prev, razao_social: d.razao_social || prev.razao_social, nome_fantasia: d.nome_fantasia || prev.nome_fantasia, nome: prev.nome || d.razao_social, cnpj_status: d.cnpj_status || prev.cnpj_status, data_abertura: d.data_abertura || prev.data_abertura, cep: d.cep || prev.cep, endereco: d.endereco || prev.endereco, numero: d.numero || prev.numero, complemento: d.complemento || prev.complemento, bairro: d.bairro || prev.bairro, cidade: d.cidade || prev.cidade, estado: d.estado || prev.estado }));
      toast.success("Dados do CNPJ carregados!");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao consultar CNPJ")); }
    finally { setLookupLoading(p => ({ ...p, cnpj: false })); }
  };

  const lookupCEP = async () => {
    const cep = form.cep.replace(/\D/g, "");
    if (cep.length !== 8) { toast.error("CEP deve ter 8 dígitos"); return; }
    setLookupLoading(p => ({ ...p, cep: true }));
    try {
      const res = await axios.get(`${API}/lookup/cep/${cep}`, { headers });
      const d = res.data;
      setForm(prev => ({ ...prev, endereco: d.endereco || prev.endereco, bairro: d.bairro || prev.bairro, cidade: d.cidade || prev.cidade, estado: d.estado || prev.estado }));
      toast.success("Endereço preenchido!");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao consultar CEP")); }
    finally { setLookupLoading(p => ({ ...p, cep: false })); }
  };

  const handleAddCargo = async () => {
    if (!novoCargo.trim()) return;
    try {
      const res = await axios.post(`${API}/cargos-contato`, { nome: novoCargo.trim() }, { headers });
      setCargos(prev => [...prev, res.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      set("cargo_contato", res.data.nome);
      setNovoCargo("");
      toast.success("Cargo adicionado!");
    } catch { toast.error("Erro ao adicionar cargo"); }
  };

  const handleAddTipoFornecimento = () => {
    if (!novoTipoFornecimento.trim()) return;
    const val = novoTipoFornecimento.trim().toLowerCase().replace(/\s+/g, "_");
    const label = novoTipoFornecimento.trim();
    if (!tiposFornecimento.find(t => t.value === val)) {
      setTiposFornecimento(prev => [...prev, { value: val, label }]);
    }
    set("tipos_fornecimento", [...(form.tipos_fornecimento || []), val]);
    setNovoTipoFornecimento("");
  };

  const handleDocUpload = (e, field) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(prev => ({ ...prev, [field]: [...(prev[field] || []), { nome: file.name, arquivo_base64: ev.target.result }] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeDoc = (field, index) => {
    setForm(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.documento) { toast.error("Nome e Documento são obrigatórios"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        await axios.put(`${API}/clientes-fornecedores/${id}`, payload, { headers });
        toast.success("Registro atualizado!");
      } else {
        await axios.post(`${API}/clientes-fornecedores`, payload, { headers });
        toast.success("Registro criado!");
      }
      navigate("/cadastros/clientes-fornecedores");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const isCliente = form.tipo_cadastro === "cliente" || form.tipo_cadastro === "ambos";
  const isFornecedor = form.tipo_cadastro === "fornecedor" || form.tipo_cadastro === "ambos";
  const isJuridica = form.tipo_pessoa === "juridica";
  const isPublico = form.setor_cliente === "publico";

  return (
    <div className="space-y-4" data-testid="entidade-form">
      <div className="flex items-center gap-3">
        <Link to="/cadastros/clientes-fornecedores"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
        <h1 className="text-xl font-heading font-bold text-[#0A1628]">{isEdit ? "Editar Cadastro" : "Novo Cadastro"}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 bg-[#0A1628]">
            <TabsTrigger value="geral" className="text-white data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#0A1628] text-xs md:text-sm">
              <Building className="h-4 w-4 mr-1 hidden md:inline" />Geral
            </TabsTrigger>
            <TabsTrigger value="contatos" className="text-white data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#0A1628] text-xs md:text-sm">
              <Phone className="h-4 w-4 mr-1 hidden md:inline" />Contatos
            </TabsTrigger>
            <TabsTrigger value="setor" className="text-white data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#0A1628] text-xs md:text-sm">
              <Briefcase className="h-4 w-4 mr-1 hidden md:inline" />Setor
            </TabsTrigger>
          </TabsList>

          {/* === TAB 1: GERAL & ENDEREÇO === */}
          <TabsContent value="geral" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Dados da Entidade</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Pessoa *</Label>
                    <NativeSelect value={form.tipo_pessoa} onValueChange={v => set("tipo_pessoa", v)} data-testid="tipo-pessoa"
                      options={[{ value: "fisica", label: "Pessoa Física" }, { value: "juridica", label: "Pessoa Jurídica" }]} />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Cadastro *</Label>
                    <NativeSelect value={form.tipo_cadastro} onValueChange={v => set("tipo_cadastro", v)} data-testid="tipo-cadastro"
                      options={[{ value: "cliente", label: "Cliente" }, { value: "fornecedor", label: "Fornecedor" }, { value: "ambos", label: "Ambos" }]} />
                  </div>
                  <div className="space-y-2">
                    <Label>{isJuridica ? "CNPJ *" : "CPF *"}</Label>
                    <div className="flex gap-2">
                      <Input value={form.documento} onChange={e => set("documento", isJuridica ? maskCNPJ(e.target.value) : maskCPF(e.target.value))} placeholder={isJuridica ? "00.000.000/0000-00" : "000.000.000-00"} data-testid="documento" />
                      {isJuridica && (
                        <Button type="button" variant="outline" size="sm" onClick={lookupCNPJ} disabled={lookupLoading.cnpj} className="shrink-0" data-testid="btn-cnpj-lookup">
                          {lookupLoading.cnpj ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                    {form.cnpj_status && <p className={`text-xs ${form.cnpj_status === "Ativa" ? "text-emerald-600" : "text-red-600"}`}>Status Receita: {form.cnpj_status}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isJuridica ? "Razão Social" : "Nome Completo *"}</Label>
                    <Input value={isJuridica ? form.razao_social : form.nome} onChange={e => set(isJuridica ? "razao_social" : "nome", e.target.value)} data-testid="razao-social" />
                  </div>
                  <div className="space-y-2">
                    <Label>{isJuridica ? "Nome Fantasia *" : "Apelido"}</Label>
                    <Input value={form.nome_fantasia || ""} onChange={e => { set("nome_fantasia", e.target.value); if (isJuridica) set("nome", e.target.value); }} data-testid="nome-fantasia" />
                  </div>
                </div>
                {isJuridica && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Inscrição Estadual</Label><Input value={form.inscricao_estadual} onChange={e => set("inscricao_estadual", maskIE(e.target.value))} /></div>
                    <div className="space-y-2"><Label>Inscrição Municipal</Label><Input value={form.inscricao_municipal} onChange={e => set("inscricao_municipal", maskIE(e.target.value))} /></div>
                    <div className="space-y-2"><Label>Data de Abertura</Label><Input type="date" value={form.data_abertura} onChange={e => set("data_abertura", e.target.value)} /></div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Endereço</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <div className="flex gap-2">
                      <Input value={form.cep} onChange={e => set("cep", maskCEP(e.target.value))} placeholder="00000-000" data-testid="cep" />
                      <Button type="button" variant="outline" size="sm" onClick={lookupCEP} disabled={lookupLoading.cep} className="shrink-0" data-testid="btn-cep-lookup">
                        {lookupLoading.cep ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2"><Label>Logradouro</Label><Input value={form.endereco} onChange={e => set("endereco", e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2"><Label>Número</Label><Input value={form.numero} onChange={e => set("numero", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Complemento</Label><Input value={form.complemento} onChange={e => set("complemento", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Bairro</Label><Input value={form.bairro} onChange={e => set("bairro", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Cidade / UF</Label>
                    <div className="flex gap-2"><Input value={form.cidade} onChange={e => set("cidade", e.target.value)} className="flex-1" /><Input value={form.estado} onChange={e => set("estado", e.target.value)} className="w-16" maxLength={2} /></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><GitBranch className="h-5 w-5" />Hierarquia Matriz / Filial</CardTitle></CardHeader>
              <CardContent>
                <NativeSelect value={form.matriz_id} onValueChange={v => set("matriz_id", v)} placeholder="Nenhuma (esta é uma matriz)"
                  options={[{ value: "", label: "Nenhuma (esta é uma matriz)" }, ...entidades.filter(e => e.tipo_pessoa === "juridica").map(e => ({ value: e.id, label: `${e.nome} - ${formatDoc(e.documento)}` }))]} />
              </CardContent>
            </Card>

            {/* Documentos do Cliente */}
            {isCliente && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><FileText className="h-5 w-5" />Documentos do Cliente</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {(form.documentos_cliente || []).map((doc, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="text-sm flex-1 truncate">{doc.nome}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeDoc("documentos_cliente", i)}><X className="h-3 w-3" /></Button>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 p-3 border-2 border-dashed rounded cursor-pointer hover:border-[#C9A84C] transition-colors">
                    <Upload className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-500">Adicionar documento (PDF)</span>
                    <input type="file" accept=".pdf,image/*" multiple onChange={e => handleDocUpload(e, "documentos_cliente")} className="hidden" />
                  </label>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* === TAB 2: CONTATOS & FINANCEIRO === */}
          <TabsContent value="contatos" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628]">Contatos</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Telefone / WhatsApp 1</Label><Input value={form.telefone_whatsapp_1} onChange={e => set("telefone_whatsapp_1", maskPhone(e.target.value))} placeholder="(00) 00000-0000" data-testid="telefone1" /></div>
                  <div className="space-y-2"><Label>Telefone / WhatsApp 2</Label><Input value={form.telefone_whatsapp_2} onChange={e => set("telefone_whatsapp_2", maskPhone(e.target.value))} placeholder="(00) 00000-0000" /></div>
                </div>
                <div className="space-y-2"><Label>E-mail</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="email" /></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Pessoa de Contato</Label><Input value={form.pessoa_contato} onChange={e => set("pessoa_contato", e.target.value)} /></div>
                  <div className="space-y-2">
                    <Label>Cargo do Contato</Label>
                    <div className="flex gap-2">
                      <NativeSelect value={form.cargo_contato} onValueChange={v => set("cargo_contato", v)} className="flex-1" placeholder="Selecione o cargo..."
                        options={cargos.map(c => ({ value: c.nome, label: c.nome }))} />
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Input value={novoCargo} onChange={e => setNovoCargo(e.target.value)} placeholder="Novo cargo..." className="text-sm h-8" />
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={handleAddCargo} disabled={!novoCargo.trim()}>
                        <Plus className="h-3 w-3 mr-1" />Adicionar
                      </Button>
                    </div>
                  </div>
                </div>

                {isFornecedor && (
                  <>
                    <Separator />
                    <p className="text-sm font-medium text-slate-600">Contatos do Fornecedor / Representante</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>Nome do Representante</Label><Input value={form.representante_nome} onChange={e => set("representante_nome", e.target.value)} /></div>
                      <div className="space-y-2"><Label>Telefone/WhatsApp Representante</Label><Input value={form.representante_telefone} onChange={e => set("representante_telefone", maskPhone(e.target.value))} placeholder="(00) 00000-0000" /></div>
                      <div className="space-y-2"><Label>E-mail do Representante</Label><Input type="email" value={form.representante_email} onChange={e => set("representante_email", e.target.value)} /></div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><DollarSign className="h-5 w-5" />Dados Financeiros</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Pessoa de Contato Financeiro</Label><Input value={form.contato_financeiro} onChange={e => set("contato_financeiro", e.target.value)} /></div>
                  <div className="space-y-2"><Label>E-mail Financeiro</Label><Input type="email" value={form.email_financeiro} onChange={e => set("email_financeiro", e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Condição de Pagamento</Label><Input value={form.condicao_pagamento} onChange={e => set("condicao_pagamento", e.target.value)} placeholder="Ex: 30/60/90 dias" /></div>
                  <div className="space-y-2">
                    <Label>Banco</Label>
                    <NativeSelect value={form.banco} onValueChange={v => set("banco", v)} placeholder="Selecione o banco..."
                      options={bancos.map(b => ({ value: `${b.codigo} - ${b.nome}`, label: `${b.codigo} - ${b.nome}` }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Agência</Label><Input value={form.agencia} onChange={e => set("agencia", e.target.value)} /></div>
                  <div className="space-y-2"><Label>Conta</Label><Input value={form.conta} onChange={e => set("conta", e.target.value)} /></div>
                </div>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Chave PIX</Label>
                    <NativeSelect value={form.tipo_pix} onValueChange={v => set("tipo_pix", v)} placeholder="Selecione o tipo..."
                      options={[{ value: "cpf", label: "CPF" }, { value: "telefone", label: "Telefone" }, { value: "email", label: "E-mail" }, { value: "aleatoria", label: "Aleatória" }]} />
                  </div>
                  <div className="space-y-2"><Label>Chave PIX</Label><Input value={form.pix} onChange={e => set("pix", e.target.value)} /></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB 3: SETOR / FORNECIMENTO === */}
          <TabsContent value="setor" className="space-y-4">
            {isCliente && isJuridica && (
              <Card className="border-0 shadow-sm">
                <CardHeader><CardTitle className="text-lg text-[#0A1628]">Setor do Cliente</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <NativeSelect value={form.setor_cliente} onValueChange={v => set("setor_cliente", v)}
                    options={[{ value: "", label: "Selecione..." }, { value: "publico", label: "Setor Público" }, { value: "privado", label: "Setor Privado" }]} />
                  {isPublico && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2"><Label>Esfera</Label>
                        <NativeSelect value={form.esfera} onValueChange={v => set("esfera", v)} options={[{ value: "", label: "Selecione..." }, { value: "federal", label: "Federal" }, { value: "estadual", label: "Estadual" }, { value: "municipal", label: "Municipal" }]} />
                      </div>
                      <div className="space-y-2"><Label>Poder</Label>
                        <NativeSelect value={form.poder} onValueChange={v => set("poder", v)} options={[{ value: "", label: "Selecione..." }, { value: "executivo", label: "Executivo" }, { value: "legislativo", label: "Legislativo" }, { value: "judiciario", label: "Judiciário" }]} />
                      </div>
                      <div className="space-y-2"><Label>Unidade Específica</Label><Input value={form.unidade_especifica} onChange={e => set("unidade_especifica", e.target.value)} placeholder="Ex: Hospital, Delegacia" /></div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {isFornecedor && (
              <>
                <Card className="border-0 shadow-sm">
                  <CardHeader><CardTitle className="text-lg text-[#0A1628]">Detalhes do Fornecimento</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>Categoria</Label>
                        <NativeSelect value={form.categoria_fornecedor} onValueChange={v => set("categoria_fornecedor", v)}
                          options={[{ value: "", label: "Selecione..." }, { value: "atacado", label: "Atacado" }, { value: "varejo", label: "Varejo" }]} />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <input type="checkbox" checked={form.is_prestador_servicos || false} onChange={e => set("is_prestador_servicos", e.target.checked)} className="rounded" />
                          Prestador de Serviços
                        </Label>
                        {form.is_prestador_servicos && (
                          <Input value={form.especializacao_prestador} onChange={e => set("especializacao_prestador", e.target.value)} placeholder="Qual especialização?" className="mt-2" />
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Tipos de Fornecimento</Label>
                      <MultiSelect values={form.tipos_fornecimento || []} onValuesChange={v => set("tipos_fornecimento", v)} placeholder="Selecione os tipos..." options={tiposFornecimento} data-testid="tipos-fornecimento" />
                      <div className="flex gap-2 mt-1">
                        <Input value={novoTipoFornecimento} onChange={e => setNovoTipoFornecimento(e.target.value)} placeholder="Novo tipo de fornecimento..." className="text-sm h-8" />
                        <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={handleAddTipoFornecimento} disabled={!novoTipoFornecimento.trim()}>
                          <Plus className="h-3 w-3 mr-1" />Adicionar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm border-l-4 border-l-[#C9A84C]">
                  <CardHeader><CardTitle className="text-lg text-[#0A1628] flex items-center gap-2"><Shield className="h-5 w-5 text-[#C9A84C]" />Homologação</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <NativeSelect value={form.homologacao_status} onValueChange={v => set("homologacao_status", v)}
                      options={[{ value: "", label: "Selecione..." }, { value: "ativo", label: "Ativo" }, { value: "bloqueado", label: "Bloqueado" }, { value: "em_analise", label: "Em Análise" }]} data-testid="homologacao-status" />
                    <Separator />
                    <p className="text-sm font-medium text-slate-600">Validade dos Documentos</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[{ field: "doc_cnd_federal", label: "CND Federal" }, { field: "doc_cnd_fgts", label: "CND FGTS" }, { field: "doc_cnd_trabalhista", label: "CND Trabalhista" }, { field: "doc_pgr", label: "PGR" }, { field: "doc_pcmat", label: "PCMAT" }].map(doc => (
                        <div key={doc.field} className="space-y-2">
                          <Label className="flex items-center gap-1">{doc.label}{isDocExpiringSoon(form[doc.field]) && <AlertTriangle className="h-3 w-3 text-[#C9A84C]" />}</Label>
                          <Input type="date" value={form[doc.field] || ""} onChange={e => set(doc.field, e.target.value)} className={isDocExpiringSoon(form[doc.field]) ? "border-[#C9A84C] bg-[#C9A84C]/5" : ""} />
                          {isDocExpiringSoon(form[doc.field]) && <p className="text-xs text-[#C9A84C] font-medium">Vencido ou vencendo em 30 dias</p>}
                        </div>
                      ))}
                    </div>
                    <Separator />
                    <p className="text-sm font-medium text-slate-600">Documentos de Homologação (PDF)</p>
                    {(form.documentos_homologacao || []).map((doc, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="text-sm flex-1 truncate">{doc.nome}</span>
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeDoc("documentos_homologacao", i)}><X className="h-3 w-3" /></Button>
                      </div>
                    ))}
                    <label className="flex items-center gap-2 p-3 border-2 border-dashed rounded cursor-pointer hover:border-[#C9A84C] transition-colors">
                      <Upload className="h-4 w-4 text-slate-400" /><span className="text-sm text-slate-500">Adicionar documento (PDF)</span>
                      <input type="file" accept=".pdf,image/*" multiple onChange={e => handleDocUpload(e, "documentos_homologacao")} className="hidden" />
                    </label>
                  </CardContent>
                </Card>
              </>
            )}
            {!isCliente && !isFornecedor && (
              <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-400">Selecione o tipo de cadastro na aba "Geral".</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 pt-4 pb-8">
          <Link to="/cadastros/clientes-fornecedores"><Button type="button" variant="outline">Cancelar</Button></Link>
          <Button type="submit" disabled={saving} className="bg-[#0A1628] hover:bg-[#0A1628]/90" data-testid="btn-salvar">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</> : isEdit ? "Atualizar Cadastro" : "Criar Cadastro"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ==================== VIEW ====================
function EntidadeView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem("luvcorp_token");
    axios.get(`${API}/clientes-fornecedores/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setEntity(res.data); setLoading(false); })
      .catch(() => { toast.error("Registro não encontrado"); navigate("/cadastros/clientes-fornecedores"); });
  }, [id]);

  if (loading || !entity) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const isFornecedor = entity.tipo_cadastro === "fornecedor" || entity.tipo_cadastro === "ambos";

  return (
    <div className="space-y-4" data-testid="entidade-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/cadastros/clientes-fornecedores"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
          <div>
            <h1 className="text-xl font-heading font-bold text-[#0A1628]">{entity.nome}</h1>
            {entity.razao_social && entity.razao_social !== entity.nome && <p className="text-sm text-slate-500">{entity.razao_social}</p>}
          </div>
        </div>
        <Link to={`/cadastros/clientes-fornecedores/${id}/edit`}><Button variant="outline" size="sm"><Edit className="h-4 w-4 mr-1" />Editar</Button></Link>
      </div>

      {entity.risco_alto && (
        <Card className="border-2 border-[#C9A84C] bg-[#C9A84C]/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-[#C9A84C] shrink-0" />
            <div><p className="font-medium text-[#0A1628]">Fornecedor com Risco Alto</p><p className="text-sm text-slate-600">Documentos vencidos ou vencendo em até 30 dias.</p></div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-400">Documento</p>
          <p className="font-mono font-medium">{formatDoc(entity.documento)}</p>
          {entity.cnpj_status && <Badge className={entity.cnpj_status === "Ativa" ? "bg-emerald-100 text-emerald-700 mt-1" : "bg-red-100 text-red-700 mt-1"}>{entity.cnpj_status}</Badge>}
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-400">Tipo</p>
          <Badge className={entity.tipo_cadastro === "cliente" ? "bg-blue-100 text-blue-700" : entity.tipo_cadastro === "fornecedor" ? "bg-amber-100 text-amber-700" : "bg-purple-100 text-purple-700"}>
            {entity.tipo_cadastro === "cliente" ? "Cliente" : entity.tipo_cadastro === "fornecedor" ? "Fornecedor" : "Ambos"}
          </Badge>
          {entity.is_prestador_servicos && <Badge className="bg-teal-100 text-teal-700 ml-1">Prestador</Badge>}
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-slate-400">Localização</p>
          <p className="text-sm font-medium">{entity.cidade ? `${entity.cidade}/${entity.estado}` : "-"}</p>
          {entity.endereco && <p className="text-xs text-slate-500">{entity.endereco}, {entity.numero}</p>}
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-lg">Contatos</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {entity.telefone_whatsapp_1 && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /><span className="text-sm">{entity.telefone_whatsapp_1}</span></div>}
            {entity.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="text-sm">{entity.email}</span></div>}
            {entity.pessoa_contato && <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /><span className="text-sm">{entity.pessoa_contato} {entity.cargo_contato ? `(${entity.cargo_contato})` : ""}</span></div>}
          </div>
        </CardContent>
      </Card>

      {isFornecedor && entity.tipos_fornecimento && entity.tipos_fornecimento.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg">Fornecimento</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {entity.tipos_fornecimento.map(t => <Badge key={t} className="bg-slate-100 text-slate-700">{t}</Badge>)}
            </div>
            {entity.is_prestador_servicos && entity.especializacao_prestador && <p className="text-sm text-teal-700 mt-2">Especialização: {entity.especializacao_prestador}</p>}
          </CardContent>
        </Card>
      )}

      {isFornecedor && entity.homologacao_status && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#C9A84C]">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="h-5 w-5 text-[#C9A84C]" />Homologação</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              {entity.homologacao_status === "ativo" && <Badge className="bg-emerald-100 text-emerald-700"><ShieldCheck className="h-3 w-3 mr-1" />Ativo</Badge>}
              {entity.homologacao_status === "bloqueado" && <Badge className="bg-red-100 text-red-700"><ShieldAlert className="h-3 w-3 mr-1" />Bloqueado</Badge>}
              {entity.homologacao_status === "em_analise" && <Badge className="bg-amber-100 text-amber-700">Em Análise</Badge>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[{ field: "doc_cnd_federal", label: "CND Federal" }, { field: "doc_cnd_fgts", label: "CND FGTS" }, { field: "doc_cnd_trabalhista", label: "CND Trabalhista" }, { field: "doc_pgr", label: "PGR" }, { field: "doc_pcmat", label: "PCMAT" }].map(doc => (
                <div key={doc.field} className={`p-2 rounded ${isDocExpiringSoon(entity[doc.field]) ? "bg-[#C9A84C]/10 border border-[#C9A84C]" : "bg-slate-50"}`}>
                  <p className="text-xs text-slate-500">{doc.label}</p>
                  <p className="text-sm font-medium">{entity[doc.field] ? new Date(entity[doc.field]).toLocaleDateString("pt-BR") : "-"}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(entity.banco || entity.pix || entity.condicao_pagamento) && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><DollarSign className="h-5 w-5" />Dados Financeiros</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {entity.contato_financeiro && <div><p className="text-xs text-slate-400">Contato Financeiro</p><p className="text-sm">{entity.contato_financeiro}</p></div>}
              {entity.email_financeiro && <div><p className="text-xs text-slate-400">E-mail Financeiro</p><p className="text-sm">{entity.email_financeiro}</p></div>}
              {entity.condicao_pagamento && <div><p className="text-xs text-slate-400">Condição Pgto</p><p className="text-sm">{entity.condicao_pagamento}</p></div>}
              {entity.banco && <div><p className="text-xs text-slate-400">Banco</p><p className="text-sm">{entity.banco}</p></div>}
              {entity.pix && <div><p className="text-xs text-slate-400">PIX ({entity.tipo_pix || ""})</p><p className="text-sm">{entity.pix}</p></div>}
            </div>
          </CardContent>
        </Card>
      )}

      {entity.filiais && entity.filiais.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><GitBranch className="h-5 w-5" />Filiais</CardTitle></CardHeader>
          <CardContent>
            {entity.filiais.map(f => (
              <Link key={f.id} to={`/cadastros/clientes-fornecedores/${f.id}`} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                <ChevronRight className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium">{f.nome}</span><span className="text-xs text-slate-500 font-mono">{formatDoc(f.documento)}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== ROUTER ====================
export default function ClientesFornecedoresPage() {
  return (
    <Routes>
      <Route index element={<MainLayout><EntidadesList /></MainLayout>} />
      <Route path="new" element={<MainLayout><EntidadeForm /></MainLayout>} />
      <Route path=":id" element={<MainLayout><EntidadeView /></MainLayout>} />
      <Route path=":id/edit" element={<MainLayout><EntidadeForm /></MainLayout>} />
    </Routes>
  );
}
